import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import useFetch, { FetchConfig, Status } from '../../../../../hooks/fetch.hook';
import { OrganizationAdministratorApiModel } from '../Organizations/models/organization-administrator.api-model';
import ValidationInfo from '../../../../../validation/validation-info';
import Spinner from '../../../../Shared/Spinner';
import PageIntroduction from '../../../../PaEgnaBen/Shared/PageIntroduction';
import TextFieldFormGroup from '../../../../Shared/TextFieldFormGroup';
import IValidatable, { IServerValidationError } from '../../../../Shared/validatable.interface';
import NumberFieldFormGroup from '../../../../Shared/NumberFieldFormGroup';
import Breadcumb from '../../../../PaEgnaBen/Shared/Breadcrumb';
import { OrganizationNumberHelper } from '../../../../../helpers/organization-number.helper';
import { flushSync } from 'react-dom';
import { ValidatableComponent, ValidationHelper } from '../../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../../services/analytics-service';
import ValidationSummary from '../../../../Shared/ValidationSummary';
import AntiforgeryService from '../../../../../services/anti-forgery.service';
import Alert from '../../../../Shared/Alert';
import LogService from '../../../../../services/log-service/log.service';
import { AdministratorTabType } from '../../models/administrator-tab-type.enum';

function Organization() {
  // === PARAMS ===
  const { organizationId } = useParams();
  const navigate = useNavigate();

  // === STATE ===
  const [{ status: getOrganizationStatus, data: getOrganizationData, httpResponseStatusCode: organizationHttpResponseStatusCode }] = useFetch<OrganizationAdministratorApiModel>(
    `/api/stralkastaren/administrator/organizations/${organizationId}`,
    !organizationId,
    false,
    FetchConfig.default,
  );

  const [nameState, setNameState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [contactNameState, setContactNameState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [emailState, setEmailState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [addressState, setAddressState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [postalCodeState, setPostalCodeState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [cityState, setCityState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [phoneNumberState, setPhoneNumberState] = useState({ value: '', validationInfo: new ValidationInfo() });
  const [numberOfCopiesState, setNumberOfCopiesState] = useState<{ value: number | undefined; validationInfo: ValidationInfo }>({ value: undefined, validationInfo: new ValidationInfo() });
  const [saveStatus, setSaveStatus] = useState<Status>(Status.notperformed);
  const [saveErrorCode, setSaveErrorCode] = useState<number | undefined>(undefined);

  // === REFS ===
  const nameRef = useRef<IValidatable>(null!);
  const contactNameRef = useRef<IValidatable>(null!);
  const emailRef = useRef<IValidatable>(null!);
  const streetAddressRef = useRef<IValidatable>(null!);
  const postalCodeRef = useRef<IValidatable>(null!);
  const cityRef = useRef<IValidatable>(null!);
  const phoneNumberRef = useRef<IValidatable>(null!);
  const numberOfCopiesRef = useRef<IValidatable>(null!);

  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors(getValidatableComponents());
  useEffect(() => {
    if (getOrganizationData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNameState({ value: getOrganizationData.name, validationInfo: new ValidationInfo() });
      setContactNameState({ value: getOrganizationData.contactName, validationInfo: new ValidationInfo() });
      setEmailState({ value: getOrganizationData.email, validationInfo: new ValidationInfo() });
      setAddressState({ value: getOrganizationData.streetAddress, validationInfo: new ValidationInfo() });
      setPostalCodeState({ value: getOrganizationData.postalCode, validationInfo: new ValidationInfo() });
      setCityState({ value: getOrganizationData.city, validationInfo: new ValidationInfo() });
      setPhoneNumberState({ value: getOrganizationData.phoneNumber, validationInfo: new ValidationInfo() });
      setNumberOfCopiesState({ value: getOrganizationData.numberOfCopies, validationInfo: new ValidationInfo() });
    }
  }, [getOrganizationData]);

  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: nameRef.current, validationInfo: nameState.validationInfo, setState: setNameState },
      { component: contactNameRef.current, validationInfo: contactNameState.validationInfo, setState: setContactNameState },
      { component: emailRef.current, validationInfo: emailState.validationInfo, setState: setEmailState },
      { component: streetAddressRef.current, validationInfo: addressState.validationInfo, setState: setAddressState },
      { component: postalCodeRef.current, validationInfo: postalCodeState.validationInfo, setState: setPostalCodeState },
      { component: cityRef.current, validationInfo: cityState.validationInfo, setState: setCityState },
      { component: phoneNumberRef.current, validationInfo: phoneNumberState.validationInfo, setState: setPhoneNumberState },
      { component: numberOfCopiesRef.current, validationInfo: numberOfCopiesState.validationInfo, setState: setNumberOfCopiesState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, AnalyticsService.ErrorAction, e);
    });
  }

  // === EVENT HANDLERS ===
  async function handleSaveClick() {
    let isFormValid: boolean | undefined;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (saveStatus === Status.loading) return;

      setSaveStatus(Status.notperformed);
      setSaveErrorCode(undefined);

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(AnalyticsService.StralkastarenRegistration, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      const organizationAdministratorApiModel: OrganizationAdministratorApiModel = {
        id: getOrganizationData?.id ?? (organizationId ? Number(organizationId) : -1),
        organizationNumber: getOrganizationData?.organizationNumber ?? '',
        name: nameState.value,
        contactName: contactNameState.value,
        email: emailState.value,
        streetAddress: addressState.value,
        postalCode: postalCodeState.value,
        city: cityState.value,
        phoneNumber: phoneNumberState.value,
        numberOfCopies: Number(numberOfCopiesState.value ?? 0),
      };

      try {
        const response = await fetch(`/api/stralkastaren/administrator/organizations${organizationId ? `/${organizationId}` : ''}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
          body: JSON.stringify(organizationAdministratorApiModel),
        });
        if (response.ok) {
          setSaveStatus(Status.success);
          setSaveErrorCode(undefined);
          submitResult = { status: Status.success, errorCode: undefined };
          navigate(`/?tab=${AdministratorTabType.Organization}`);
        } else {
          setSaveStatus(Status.failed);
          setSaveErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
        }
      } catch (error: unknown) {
        setSaveStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: (error as { status?: number }).status || 0 };
        LogService.logError(error, 'Error saving stralkastaren organization information');
      }
    });
    if (submitResult?.errorCode === 400) document.getElementById('organization-form-validation-summary')?.focus();
  }

  if (saveStatus === Status.failed || getOrganizationStatus === Status.failed) {
    if (saveErrorCode === 401 || organizationHttpResponseStatusCode === 401) {
      return <Navigate to="/session-utgangen" state={{ from: window.location.pathname }} />;
    }
    if (saveErrorCode === 403 || organizationHttpResponseStatusCode === 403) {
      return <Navigate to="/atkomst-saknas" />;
    }
  }

  return (
    <div className="container container--narrow">
      {getOrganizationStatus === Status.loading && <Spinner />}
      {getOrganizationStatus === Status.failed && organizationId && <div>Det gick inte att hämta information om organisationen. Försök igen senare.</div>}
      {(getOrganizationStatus === Status.success || !organizationId) && (
        <>
          <Breadcumb
            items={[
              { text: 'Administration', url: '/' },
              { text: getOrganizationData?.name ?? '', url: undefined },
            ]}
          />
          <PageIntroduction heading={getOrganizationData?.name ?? ''} text={'Org.nr: ' + OrganizationNumberHelper.formatOrganizationNumber(getOrganizationData?.organizationNumber ?? '')} />
          <form id="organization-form">
            <fieldset className="row">
              <legend className="visually-hidden">Formulär för att redigera en organisation</legend>
              <ValidationSummary id="organization-form-validation-summary" validationSummaryErrors={validationSummaryErrors} />
              <TextFieldFormGroup
                ref={nameRef}
                labelText="Namn på företag/organisation"
                required={true}
                hideRequiredAsterisk={true}
                inputId="name-input"
                type="text"
                serverValidationName="name"
                state={[nameState, setNameState]}
                maxLength={35}
              />
              <TextFieldFormGroup
                ref={contactNameRef}
                labelText="Kontaktperson"
                required={true}
                hideRequiredAsterisk={true}
                inputId="contact-name-input"
                type="text"
                serverValidationName="contactName"
                state={[contactNameState, setContactNameState]}
                maxLength={35}
              />
              <TextFieldFormGroup
                ref={emailRef}
                labelText="E-post"
                required={true}
                hideRequiredAsterisk={true}
                inputId="email-input"
                type="email"
                serverValidationName="email"
                state={[emailState, setEmailState]}
                maxLength={35}
              />
              <TextFieldFormGroup
                ref={phoneNumberRef}
                labelText="Telefonnummer"
                required={true}
                hideRequiredAsterisk={true}
                inputId="phone-number-input"
                type="text"
                serverValidationName="phoneNumber"
                state={[phoneNumberState, setPhoneNumberState]}
                maxLength={20}
              />
              <TextFieldFormGroup
                ref={streetAddressRef}
                labelText="Gatuadress"
                required={true}
                hideRequiredAsterisk={true}
                inputId="street-address-input"
                type="text"
                serverValidationName="streetAddress"
                state={[addressState, setAddressState]}
                maxLength={35}
              />
              <TextFieldFormGroup
                ref={postalCodeRef}
                labelText="Postnummer"
                required={true}
                className="col-md-4"
                hideRequiredAsterisk={true}
                inputId="postal-code-input"
                type="text"
                serverValidationName="postalCode"
                state={[postalCodeState, setPostalCodeState]}
                maxLength={35}
              />
              <TextFieldFormGroup
                ref={cityRef}
                labelText="Ort"
                required={true}
                className="col-md-8"
                hideRequiredAsterisk={true}
                inputId="city-input"
                type="text"
                serverValidationName="city"
                state={[cityState, setCityState]}
                maxLength={35}
              />
              <NumberFieldFormGroup
                ref={numberOfCopiesRef}
                labelText="Antal exemplar"
                required={true}
                className="mb-4"
                hideRequiredAsterisk={true}
                inputId="number-of-copies-input"
                serverValidationName="numberOfCopies"
                state={[numberOfCopiesState, setNumberOfCopiesState]}
                min={5}
                max={100}
              />

              <div className="d-grid d-md-flex gap-2 align-items-center">
                <button type="button" className={`btn btn-primary ${saveStatus === Status.loading ? 'btn-spinner' : ''}`} onClick={handleSaveClick}>
                  <span hidden={saveStatus === Status.loading}>Spara uppgifter</span>
                </button>
                <Link to={`/?tab=${AdministratorTabType.Organization}`} className="btn btn-link">
                  Avbryt
                </Link>
              </div>

              {saveStatus === Status.failed && saveErrorCode !== 400 && (
                <Alert show={true} heading="Kunde inte ändra organisationsuppgifterna" closable={false} className="mt-3" />
              )}
            </fieldset>
          </form>
        </>
      )}
    </div>
  );
}

export default Organization;